'use es6';

import { PureComponent } from 'react';
import { isUninitialized, getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import get from 'transmute/get';
import { AsyncCalleeOmnibusResult } from '../records/CalleeOmnibusResult';
import { getOrCreateCalleeOmnibusStore, getSubjectKey, setCalleeOmnibusData } from '../utils/getOrCreateCalleeOmnibusStore';
import { getOrCreateSettingsOmnibusStore, setSettingsOmnibusData, setProviderInSettingsOmnibus } from '../utils/getOrCreateSettingsOmnibusStore';
import { fetchCalleeOmnibus } from 'calling-lifecycle-internal/callee-omnibus/clients/fetchCalleeOmnibus';
import { getPhoneNumbers, getHubspotProvidedNumbers } from 'call-widget-addon/callee-omnibus/operators/settingsOmnibusResultGetters';
import { CALL_FROM_BROWSER } from 'calling-internal-common/constants/CallMethods';
import { getHubSpotCallingProvider } from 'calling-lifecycle-internal/call-provider/operators/callProviderOperators';
import { HUBSPOT, TWILIO } from 'calling-internal-common/call-provider/constants/ProviderNames';
import { getAppIdentifierEligibleForProviderSwitching } from '../../calling-gates/utils/getIsUngatedForBidirectionalProviderSwitching';
import { HELP_DESK, ACTIVITY_CREATOR } from 'calling-internal-common/constants/appIdentifiers';
import { jsx as _jsx } from "react/jsx-runtime";
const withCalleeOmnibus = Component => {
  class Wrapper extends PureComponent {
    constructor(props) {
      super(props);
      this.getEntry = ({
        calleeOmnibusEntries
      } = this.state) => {
        return calleeOmnibusEntries.get(getSubjectKey(this.props));
      };
      this.handleCalleeOmnibusUpdatedState = ({
        instance: calleeOmnibusEntries
      }) => {
        this.setState({
          calleeOmnibusEntries: calleeOmnibusEntries.getState()
        });
      };
      this.handleSettingsOmnibusUpdatedState = ({
        instance: settingsOmnibusEntries
      }) => {
        this.setState({
          settingsOmnibusEntries: settingsOmnibusEntries.getState()
        });
      };
      this.refreshCalleeOmnibus = ({
        subjectId,
        objectTypeId,
        gates,
        appIdentifier,
        initializingOmnibus = false
      }) => {
        const fetchCalleeOmnibusPromise = fetchCalleeOmnibus({
          objectId: subjectId,
          objectTypeId
        });
        setCalleeOmnibusData({
          fetchCalleeOmnibusPromise,
          subjectId,
          objectTypeId
        });
        setSettingsOmnibusData({
          fetchCalleeOmnibusPromise,
          initializingOmnibus
        });
        if (getAppIdentifierEligibleForProviderSwitching(appIdentifier)) {
          setProviderInSettingsOmnibus({
            fetchCalleeOmnibusPromise,
            gates,
            appIdentifier
          });
        }
      };
      this.calleeOmnibusStore = getOrCreateCalleeOmnibusStore(props);
      this.settingsOmnibusStore = getOrCreateSettingsOmnibusStore(props);
      const subjectKey = getSubjectKey(props);

      // initialState only initializes a new entry when the store is first created
      if (subjectKey && !this.calleeOmnibusStore.getState().get(subjectKey)) {
        this.calleeOmnibusStore.updateState(state => state.set(subjectKey, new AsyncCalleeOmnibusResult()));
      }
      this.state = {
        calleeOmnibusEntries: this.calleeOmnibusStore.getState(),
        settingsOmnibusEntries: this.settingsOmnibusStore.getState()
      };
    }
    componentDidMount() {
      const {
        subjectId,
        objectTypeId,
        gates,
        appIdentifier
      } = this.props;
      const calleeOmnibusEntry = this.getEntry();
      this.calleeOmnibusStore.addListener(this.handleCalleeOmnibusUpdatedState);
      this.settingsOmnibusStore.addListener(this.handleSettingsOmnibusUpdatedState);
      if ((isUninitialized(get('state', calleeOmnibusEntry)) || isUninitialized(get('state', 'asyncSettingsOmnibus'))) && subjectId && objectTypeId) {
        this.refreshCalleeOmnibus({
          subjectId,
          objectTypeId,
          gates,
          initializingOmnibus: true,
          appIdentifier
        });
      }
    }
    componentWillUnmount() {
      this.calleeOmnibusStore.removeListener(this.handleCalleeOmnibusUpdatedState);
      this.settingsOmnibusStore.removeListener(this.handleSettingsOmnibusUpdatedState);
    }
    findHubSpotCallingChannelNumber() {
      const {
        settingsOmnibusEntries
      } = this.state;
      const {
        inboxId
      } = this.props;
      const omnibusData = getData(settingsOmnibusEntries.get('asyncSettingsOmnibus'));
      const hubspotProvidedNumbers = getHubspotProvidedNumbers(getPhoneNumbers(omnibusData));
      if (hubspotProvidedNumbers && hubspotProvidedNumbers.length) {
        return hubspotProvidedNumbers.find(hsNumber => {
          const workspaceObject = hsNumber.get('workspaceIdAndType');
          return workspaceObject && workspaceObject.workspaceId === inboxId;
        });
      }
      return null;
    }
    getCallingProviderForActivityCreator() {
      const {
        settingsOmnibusEntries
      } = this.state;
      // first get the callProvider setting
      const callProvider = settingsOmnibusEntries.get('callProvider');
      // if the call provider is not HUBSPOT or TWILIO

      if (callProvider && (callProvider.name !== HUBSPOT || callProvider.name !== TWILIO)) {
        // default to HUBSPOT

        return getHubSpotCallingProvider();
      }
      return callProvider;
    }
    render() {
      const {
        settingsOmnibusEntries
      } = this.state;
      const isHelpdesk = this.props.appIdentifier === HELP_DESK;
      const isActivityCreator = this.props.appIdentifier === ACTIVITY_CREATOR;
      return /*#__PURE__*/_jsx(Component, Object.assign({}, this.props, {
        asyncCalleeOmnibus: this.getEntry(),
        asyncSettingsOmnibus: settingsOmnibusEntries.get('asyncSettingsOmnibus'),
        selectedCallProvider: isHelpdesk ? getHubSpotCallingProvider() : isActivityCreator ? this.getCallingProviderForActivityCreator() : settingsOmnibusEntries.get('callProvider'),
        selectedFromNumber: isHelpdesk ? this.findHubSpotCallingChannelNumber() : settingsOmnibusEntries.get('fromNumber'),
        selectedCallMethod: isHelpdesk || isActivityCreator ? CALL_FROM_BROWSER : settingsOmnibusEntries.get('callMethod'),
        refreshCalleeOmnibus: this.refreshCalleeOmnibus
      }));
    }
  }
  Wrapper.displayName = 'WithCalleeOmnibus';
  return Wrapper;
};
export default withCalleeOmnibus;